import { type FC, useState } from 'react';

import Info from '@crehana/compass.icons.fill/Info';
import ArrowIosDown from '@crehana/compass.icons.outline/ArrowIosDown';
import ArrowIosUp from '@crehana/compass.icons.outline/ArrowIosUp';
import Clock from '@crehana/compass.icons.outline/Clock';
import { Collapse, type TTypeProject } from '@crehana/compass.ui';
import { cn } from '@crehana/compass.ui/v2';
import { boldFormatter } from '@crehana/i18n';
import { clsx } from 'clsx';

import type { StatusProjectEnum } from '@/shared/types/graphql/globals.v5';

import useTeamCoursesTranslation from '../../hooks/useTeamCoursesTranslation/useTeamCoursesTranslation';
import { customType, type TLayout } from '../../types';
import { LayoutCardProject } from '../CardProject/LayoutCardProject';
import CardTrackDisabled from '../CardTrackDisabled';
import { TrackCompletionCriteria } from '../track-completion-criteria';
import CardDownloadCertificate from './CardDownloadCertificate';
import { LegacyCertificationCriteria } from './legacy-certification-criteria';

export const Layout: FC<TLayout> = ({
  summaryCourse,
  summaryProject,
  summaryQuiz,
  teamDisabled,
  dataCertificate,
  isCompletedTrack,
  trackProjects,
  hasCertificate,
  isOpenModality,
  isTrackCompletionRulesEnabled,
  completionRequirements = [],
  projectCoursesCount,
  quizCoursesCount,
  pendingQuizCourseUrl,
  isTrackClosed,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const { t } = useTeamCoursesTranslation();

  // Una ruta sin regla configurada hace que la query responda con error, así
  // que la lista vacía cae a las filas legacy y el panel nunca queda en blanco.
  const hasConfiguredCompletionRule =
    Boolean(isTrackCompletionRulesEnabled) && completionRequirements.length > 0;

  const shouldRenderCardDownloadCertificate =
    isCompletedTrack && hasCertificate && dataCertificate;

  const shouldRenderCertificatePendingClosureNotice =
    hasCertificate &&
    !isCompletedTrack &&
    isOpenModality &&
    // Contradice al aviso de generacion automatica, solo uno puede verse.
    !hasConfiguredCompletionRule;

  // A diferencia del aviso al que reemplaza, este no se acota por
  // isOpenModality: hasConfiguredCompletionRule ya implica que la organizacion
  // configuro una regla de completitud, y esa regla es la que decide la
  // certificacion en cualquier modalidad.
  const shouldRenderAutomaticCertificateNotice =
    hasConfiguredCompletionRule && hasCertificate && !isCompletedTrack;

  const toogleTitleProject = (
    type?: StatusProjectEnum | null,
  ): string | undefined => {
    // biome-ignore lint/style/useBlockStatements: concise single-line form preferred here
    if (type) return t(`PROJECT_TYPE_${type}`);
  };

  const toogleTypeProject = (
    type?: StatusProjectEnum | null,
  ): TTypeProject | undefined => {
    if (type) {
      return customType[type];
    }
  };

  const handleClickCollapse = () => {
    setIsOpen(!isOpen);
  };

  const redirectProject = (url?: string | null) => {
    // biome-ignore lint/style/useBlockStatements: concise single-line form preferred here
    if (url) window.open(url, '_blank');
  };

  const selectIconArrow = () =>
    isOpen ? (
      <ArrowIosUp size={20} className="tw-text-current" />
    ) : (
      <ArrowIosDown size={20} className="tw-text-current" />
    );

  return (
    <>
      {!teamDisabled ? (
        <div
          className={cn(
            'tw-bg-neutral-light-100 tw-p-2 tw-rounded-1 dark:tw-bg-elevation-dark-04',
            isOpen ? 'tw-h-fit' : 'tw-h-6.4',
          )}
        >
          <div className=" tw-flex tw-items-center tw-justify-between">
            <p className="tw-font-subtitle5 tw-text-neutral-light-800 dark:tw-text-neutral-light-100">
              {t('LEARN_TEAM_CRITERIA')}
            </p>
            <button
              className="tw-text-neutral-light-700 dark:tw-text-neutral-dark-400"
              onClick={handleClickCollapse}
              type="button"
            >
              {selectIconArrow()}
            </button>
          </div>
          <div
            className={clsx(
              isOpen ? 'tw-pt-1 tw-mt-1.2' : 'tw-pt-0 tw-mt-0 tw-opacity-0',
            )}
          >
            <Collapse
              open={isOpen}
              animated
              className="!tw-h-full !tw-overflow-visible"
            >
              {hasConfiguredCompletionRule ? (
                <TrackCompletionCriteria
                  requirements={completionRequirements}
                  projectCoursesCount={projectCoursesCount}
                  quizCoursesCount={quizCoursesCount}
                  requiredCourses={summaryCourse.required}
                  pendingQuizCourseUrl={pendingQuizCourseUrl}
                  isTrackClosed={isTrackClosed}
                />
              ) : (
                <LegacyCertificationCriteria
                  summaryCourse={summaryCourse}
                  summaryQuiz={summaryQuiz}
                  summaryProject={summaryProject}
                />
              )}
              {shouldRenderAutomaticCertificateNotice && (
                <div className="tw-flex tw-items-center tw-gap-0.8 tw-pt-1.2 tw-mb-1.2 tw-border-t tw-border-dashed tw-border-neutral-light-300">
                  <Clock
                    size={16}
                    className="tw-text-neutral-light-700 tw-shrink-0"
                  />
                  <p className="tw-font-body5 tw-text-neutral-light-700 dark:tw-text-neutral-dark-400">
                    {t('LEARN_TEAM_RULE_CERTIFICATE_AUTOMATIC')}
                  </p>
                </div>
              )}
              {summaryProject.required > 0 && trackProjects && (
                <LayoutCardProject
                  key={trackProjects?.originalId}
                  type={toogleTypeProject(trackProjects?.currentActionState)}
                  typeTitle={toogleTitleProject(
                    trackProjects?.currentActionState,
                  )}
                  title={trackProjects?.title || ''}
                  logo={trackProjects?.projectImageThumbnailUrl || ''}
                  handleClick={() =>
                    redirectProject(trackProjects?.url.relativeUrl)
                  }
                />
              )}
              {!hasCertificate && (
                <div className="tw-text-neutral-light-700 dark:tw-text-neutral-dark-400 tw-flex tw-justify-start tw-gap-0.8 tw-items-center tw-p-1.2 tw-border tw-border-neutral-light-500 tw-border-solid tw-rounded-0.8 tw-bg-neutral-light-200 tw-w-fit">
                  <Info size={24} className="tw-text-current" />
                  <p className="tw-font-body5">
                    {t('THIS_TRACK_DOES_NOT_PROVIDE_CERTIFICATE', {
                      formatter: boldFormatter,
                    })}
                  </p>
                </div>
              )}
              {shouldRenderCertificatePendingClosureNotice && (
                <div className="tw-cds-text-info tw-flex tw-justify-start tw-gap-0.8 tw-items-center tw-p-1.2 tw-cds-rounded-xs tw-cds-bg-info tw-w-fit">
                  <Info size={24} className="tw-text-current tw-shrink-0" />
                  <p className="tw-font-body5">
                    {t('LEARN_TEAM_CRITERIA_CERTIFICATE_PENDING_CLOSURE')}
                  </p>
                </div>
              )}
              {shouldRenderCardDownloadCertificate &&
                dataCertificate?.attachmentUrl && (
                  <CardDownloadCertificate
                    urlAttachmenteCertificate={dataCertificate?.attachmentUrl}
                  />
                )}
            </Collapse>
          </div>
        </div>
      ) : (
        <CardTrackDisabled />
      )}
    </>
  );
};
